import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ApiResponse,
  GenerateAdvancedBillingRequest,
  GenerateAdvancedBillingResponse,
  GetAdvancedBillingInvoicesRequest,
  PaginatedAdvancedBillingInvoicesResponse,
  GetAdvancedBillingLogsRequest,
  PaginatedAdvancedBillingLogsResponse,
  CompanyBillingStatus,
  CompanyBillingStatusById
} from '../shared/models/advance-billing-invoice.model';

@Injectable({
  providedIn: 'root'
})
export class AdvanceBillingInvoiceService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.superUserBaseUrl}/SuperAdminInvoice`;

  /**
   * API 1: Generate Advanced Billing Invoices
   * Triggers the monthly billing process to charge companies based on their active patients
   */
  generateAdvancedBillingInvoices(
    request: GenerateAdvancedBillingRequest
  ): Observable<ApiResponse<GenerateAdvancedBillingResponse>> {
    return this.http.post<ApiResponse<GenerateAdvancedBillingResponse>>(
      `${this.apiUrl}/GenerateAdvancedBillingInvoicesV2`,
      request
    );
  }

  /**
   * API 2: Get Advanced Billing Invoices
   * Retrieves paginated list of all generated invoices with date filtering
   */
  getAdvancedBillingInvoices(
    request: GetAdvancedBillingInvoicesRequest = {}
  ): Observable<ApiResponse<PaginatedAdvancedBillingInvoicesResponse>> {
    return this.http.post<ApiResponse<PaginatedAdvancedBillingInvoicesResponse>>(
      `${this.apiUrl}/GetAdvancedBillingInvoices`,
      request
    );
  }

  /**
   * API 3: Get Advanced Billing Logs
   * Retrieves detailed execution logs for billing runs with comprehensive filtering
   */
  getAdvancedBillingLogs(
    request: GetAdvancedBillingLogsRequest = {}
  ): Observable<ApiResponse<PaginatedAdvancedBillingLogsResponse>> {
    let params = new HttpParams();

    if (request.pageNumber !== undefined) {
      params = params.set('pageNumber', request.pageNumber.toString());
    }
    if (request.pageSize !== undefined) {
      params = params.set('pageSize', request.pageSize.toString());
    }
    if (request.companyId) {
      params = params.set('companyId', request.companyId);
    }
    if (request.billingPeriodMonth !== undefined) {
      params = params.set('billingPeriodMonth', request.billingPeriodMonth.toString());
    }
    if (request.billingPeriodYear !== undefined) {
      params = params.set('billingPeriodYear', request.billingPeriodYear.toString());
    }
    if (request.status) {
      params = params.set('status', request.status);
    }
    if (request.processingStep) {
      params = params.set('processingStep', request.processingStep);
    }
    if (request.forceExecuted !== undefined) {
      params = params.set('forceExecuted', request.forceExecuted.toString());
    }
    if (request.isManualTrigger !== undefined) {
      params = params.set('isManualTrigger', request.isManualTrigger.toString());
    }
    if (request.billingRunId) {
      params = params.set('billingRunId', request.billingRunId);
    }
    if (request.startDate) {
      params = params.set('startDate', request.startDate);
    }
    if (request.endDate) {
      params = params.set('endDate', request.endDate);
    }

    return this.http.get<ApiResponse<PaginatedAdvancedBillingLogsResponse>>(`${this.apiUrl}/GetAdvancedBillingLogs`, {
      params
    });
  }

  /**
   * API 4: Get Company Billing Status
   * Retrieves billing status for all companies
   */
  getCompanyBillingStatus(): Observable<ApiResponse<CompanyBillingStatus[]>> {
    return this.http.get<ApiResponse<CompanyBillingStatus[]>>(`${this.apiUrl}/GetCompanyBillingStatus`);
  }

  /**
   * API 5: Get Company Billing Status By ID
   * Retrieves billing status for a specific company
   */
  getCompanyBillingStatusById(companyId: string): Observable<ApiResponse<CompanyBillingStatusById>> {
    return this.http.get<ApiResponse<CompanyBillingStatusById>>(
      `${this.apiUrl}/GetCompanyBillingStatusById/${companyId}`
    );
  }
}
